import debug from 'debug';

/**
 * Namespace color palette — one color per module for easy scanning.
 * Uses CSS color strings (hex or named). The debug package applies these
 * via %c formatting in the browser DevTools console.
 *
 * To change a color: update the value here and reload.
 */
const NAMESPACE_COLORS: Record<string, string> = {
    // Player adapters
    'ShakaAdapter':            '#E91E63', // pink
    'VideoJsAdapter':          '#2196F3', // blue
    'TheoAdapter':             '#9C27B0', // purple

    // Electron
    'ElectronBridge':          '#4CAF50', // green
    'ElectronTopologyService': '#8BC34A', // light green

    // Network / geo
    'NetworkPathState':        '#FF9800', // orange
    'NetworkTopologyService':  '#FF5722', // deep orange
    'GeoLocationService':      '#FF7043', // deep orange light
    'SmartGeoResolver':        '#E64A19', // deep orange dark
    'MaxMindProvider':         '#6D4C41', // brown dark
    'IpApiProvider':           '#8D6E63', // brown light
    'GeoProviderFactory':      '#A1887F', // brown lighter
    'DnsService':              '#795548', // brown
    'DeepPacketAnalyser':      '#F44336', // red

    // Services
    'QuortexService':          '#FF6F00', // amber dark
    'UpdateCoordinator':       '#FFC107', // amber
    'ThemeManager':            '#9E9E9E', // grey

    // UI components
    'MapPanel':                '#00BCD4', // cyan
    'StrategyPanel':           '#3F51B5', // indigo
    'CDNPerformancePanel':     '#5C6BC0', // indigo light
    'ChatPanel':               '#7986CB', // indigo lighter
    'ContentSteering':         '#1976D2', // blue dark
    'BakeOffView':             '#42A5F5', // blue light
    'GeoMap':                  '#7E57C2', // deep purple
    'StreamControls':          '#AB47BC', // purple medium
    'VideoPlayerFactory':      '#B0BEC5', // blue-grey light

    // App root
    'App':                     '#FFFFFF', // white
};

/**
 * Creates a namespaced logger for a component or service.
 *
 * - log()   is gated by localStorage.debug (on by default)
 * - warn()  always fires
 * - error() always fires
 *
 * DevTools console — silence specific namespaces:
 *   __debug.disable()                          → all silent
 *   __debug.only('ShakaAdapter')               → only Shaka
 *   __debug.except('NetworkPathState')         → all except NetworkPathState
 *   localStorage.debug = 'oli:*'              → all on
 *   localStorage.debug = ''                   → all off
 */
export function createLogger(namespace: string) {
    const d = debug(`oli:${namespace}`);

    // Apply custom color if defined, otherwise let debug assign one automatically
    if (NAMESPACE_COLORS[namespace]) {
        (d as any).color = NAMESPACE_COLORS[namespace];
    }

    const relay = (level: 'warn' | 'error', args: unknown[]): void => {
        try {
            const api = typeof window !== 'undefined' ? (window as any).electronAPI : undefined;
            api?.log?.({ level, namespace, args });
        } catch {
            // Silently ignore serialization errors
        }
    };

    return {
        log:   d,
        warn:  (...args: unknown[]) => {
            console.warn(`[${namespace}]`, ...args);
            relay('warn', args);
        },
        error: (...args: unknown[]) => {
            console.error(`[${namespace}]`, ...args);
            relay('error', args);
        },
    };
}

// Default: all oli:* logs on — user can narrow down or disable via DevTools
if (typeof window !== 'undefined') {
    const current = localStorage.getItem('debug');
    if (!current) {
        localStorage.debug = 'oli:*';
    } else if (!current.includes('oli:*') && current !== '*') {
        localStorage.debug = `${current},oli:*`;
    }
    
    // Immediate feedback that the new logger is active
    console.log('%c[oli] Modern Logger Initialized. Individual modules will use colored namespaces.', 'color: #1976D2; font-weight: bold;');
}

// DevTools helpers
if (typeof window !== 'undefined') {
    (window as any).__debug = {
        enable:  (ns = 'oli:*')    => { localStorage.debug = ns;                        location.reload(); },
        disable: ()                 => { localStorage.debug = '';                         location.reload(); },
        only:    (ns: string)       => { localStorage.debug = `oli:${ns}`;               location.reload(); },
        except:  (ns: string)       => { localStorage.debug = `oli:*,-oli:${ns}`;        location.reload(); },
        status:  ()                 => console.log('debug =', localStorage.debug || '(disabled)'),
    };
}
